/** Only JSON data crosses a rules, projection, snapshot, or journal boundary. */
export function canonicalJson(value: unknown): string {
  const ancestors = new Set<object>();
  function visit(node: unknown, path: string): string {
    if (node === null) return 'null';
    if (typeof node === 'string' || typeof node === 'boolean') return JSON.stringify(node);
    if (typeof node === 'number' && Number.isFinite(node)) return JSON.stringify(node);
    if (typeof node !== 'object') throw new TypeError(`Non-JSON value at ${path}`);
    if (ancestors.has(node)) throw new TypeError(`Cyclic JSON value at ${path}`);
    if (Object.getOwnPropertySymbols(node).length) throw new TypeError(`Symbol property at ${path}`);
    ancestors.add(node);
    let result: string;
    if (Array.isArray(node)) {
      if (Object.keys(node).length !== node.length) throw new TypeError(`Sparse or decorated array at ${path}`);
      result = `[${Array.from({ length: node.length }, (_, index) => {
        const property = Object.getOwnPropertyDescriptor(node, String(index));
        if (!property || !('value' in property)) throw new TypeError(`Sparse array or accessor at ${path}[${index}]`);
        return visit(property.value, `${path}[${index}]`);
      }).join(',')}]`;
    } else {
      const prototype = Object.getPrototypeOf(node);
      if (prototype !== Object.prototype && prototype !== null) throw new TypeError(`Non-plain object at ${path}`);
      result = `{${Object.keys(node).sort().map((key) => {
        const property = Object.getOwnPropertyDescriptor(node, key);
        if (!property || !('value' in property)) throw new TypeError(`Accessor at ${path}.${key}`);
        return `${JSON.stringify(key)}:${visit(property.value, `${path}.${key}`)}`;
      }).join(',')}}`;
    }
    ancestors.delete(node);
    return result;
  }
  return visit(value, '$');
}

export function cloneJson<T>(value: T): T {
  return JSON.parse(canonicalJson(value)) as T;
}

/** A reproducibility checksum, not a cryptographic signature or an authorization primitive. */
export function hashJson(value: unknown): string {
  const serialized = canonicalJson(value);
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index++) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Freeze data passed to author callbacks so queries cannot accidentally mutate it. */
export function freezeJson<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const child of Object.values(value)) freezeJson(child);
    Object.freeze(value);
  }
  return value;
}
